import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Receipt,
  Users,
  Megaphone,
  Settings,
  Gift,
  X,
} from 'lucide-react'
import { cn } from '../../lib/utils'

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  disabled?: boolean
  onClick?: () => void
}

function NavItem({ to, icon, label, disabled, onClick }: NavItemProps) {
  if (disabled) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 text-slate-500 cursor-not-allowed">
        {icon}
        <span>{label}</span>
      </div>
    )
  }

  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
          isActive
            ? 'bg-doge-500/10 text-doge-400 border-l-2 border-doge-500'
            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  )
}

interface SidebarProps {
  open: boolean
  onClose: () => void
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 flex flex-col transform transition-transform lg:transform-none',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
      {/* Logo */}
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/dogici-logo.svg" alt="Dogici" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-lg text-white">Dogici</h1>
              <p className="text-xs text-slate-400">Admin Panel</p>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        <NavItem
          to="/"
          icon={<LayoutDashboard size={20} />}
          label="Dashboard"
          onClick={onClose}
        />
        <NavItem
          to="/transactions"
          icon={<Receipt size={20} />}
          label="Transactions"
          onClick={onClose}
        />
        <NavItem
          to="/system-airdrop"
          icon={<Gift size={20} />}
          label="System Airdrop"
          onClick={onClose}
        />
        <NavItem
          to="/users"
          icon={<Users size={20} />}
          label="Users"
          disabled
        />
        <NavItem
          to="/campaigns"
          icon={<Megaphone size={20} />}
          label="Campaigns"
          disabled
        />
        <NavItem
          to="/settings"
          icon={<Settings size={20} />}
          label="Settings"
          disabled
        />
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-800">
        <p className="text-xs text-slate-500 text-center">
          Dogici Admin v1.0
        </p>
      </div>
    </aside>
    </>
  )
}
